import {
    conditionsEndpoint,
    orderProductsEndpoint,
    ordersEndpoint,
    orderStationsEndpoint,
    orderAttachmentsEndpoint
} from '@/services/adsign-backend/endpoints';
import {numeric, or, and, required, requiredIf} from 'vuelidate/lib/validators';
import {customValidation, decimal, greaterThan} from '@/validators/validators';
import orderCommonsMethods from '@/mixins/orderCommonsMethods';
import utils from '@/mixins/utils';
import {OrderType} from '@/mixins/dictionariesConst';

export default {
    mixins: [orderCommonsMethods, utils],
    mounted() {
        const promises = [];
        this.loadSpotTypes();
        this.$watch(() => this.common, newCommon => this.$emit('input', this.loadSpotTypes(newCommon)), { deep: true });
        if (this.form.id) {
            promises.push(ordersEndpoint.getOrder(this.form.id).then(result => {
                this.form.status = result.data.status;
                this.form.spotTypeId = result.data.spotType?.valueId;
                this.form.priceMethod = result.data.budget ? 'budget' : 'grp';
                this.form.budget = result.data.budget;
                this.form.grp = result.data.grp;
                this.form.grpSaleTypeId = result.data.grpSaleType?.valueId;
                this.form.packageTypeId = result.data.packageType?.valueId;
                this.form.targetGroupId = result.data.targetGroup?.valueId;
                this.form.joinInvoices = result.data.paymentTogether;

                this.loadConditions();
            }));

            promises.push(orderProductsEndpoint.getOrderProducts(this.form.id).then(result => {
                this.orderProducts = result.data.map((orderProduct, index) => ({
                    id: orderProduct.id,
                    no: index + 1,
                    productId: orderProduct.product.id,
                    productName: orderProduct.product.name,
                    duration: orderProduct.duration,
                    productShare: orderProduct.productShare,
                    productCategory: orderProduct.productCategory,
                    positionShare: orderProduct.positionShare,
                    primeTimeShare: orderProduct.primeTimeShare,
                    validFrom: orderProduct.validFrom && this.$moment(orderProduct.validFrom).format('L') || null,
                    validTo: orderProduct.validTo && this.$moment(orderProduct.validTo).format('L') || null
                }));
            }))

            promises.push(orderStationsEndpoint.getOrderStations(this.form.id)
                .then(result => this.orderStations = result.data));

            promises.push(orderProductsEndpoint.getOrderProductSequences(this.form.id).then(result => {
                this.orderProductsSequences = result.data.map(seq => ({
                    id: seq.id,
                    data: seq.orderProductIds
                }));
            }));

            promises.push(orderAttachmentsEndpoint.getOrderAttachments(this.form.id)
                .then(result => this.orderAttachments = result))
        }

        Promise.all(promises).finally(() => {
            this.loading = false;

            this.$nextTick(() => {
                this.$v.$reset();
            });

            this.$watch(() => ({
                packageTypeId: this.form.packageTypeId,
                // targetGroupId: this.form.targetGroupId, // I don't know if we should watch this variable We call save function too many times
            }), (newValue, oldValue) => {
                this.$nextTick(() => {
                    this.save(true, newValue.packageTypeId != oldValue.packageTypeId);
                })
            });
        })
    },
    methods: {
        loadSpotTypes(common = this.common) {
            const validFrom = this.formatToIsoDate(common.validFrom);
            const validTo = this.formatToIsoDate(common.validTo);

            conditionsEndpoint.getSpotTypes(common.agencyId, common.clientId, this.form.orderType.shortName,
                validFrom, validTo).then(result => this.spotTypes = result.data);
        }
    },
    validations: function () {
        const isPriceMethodSelected = (priceMethod) => this.form.priceMethod == priceMethod;
        const isOohOrInternetOrder = () => {
            return this.form.orderType.shortName == OrderType.OOH || this.form.orderType.shortName == OrderType.Internet || this.form.orderType.shortName == OrderType.DA;
        }
        const orderType = (type, type2, type3, type4) => this.form.orderType.shortName == type || this.form.orderType.shortName == type2 || this.form.orderType.shortName == type3 || this.form.orderType.shortName == type4;
        const isRadioOrder =() => { return this.form.orderType.shortName == OrderType.Radio};
        return {
            form: {
                spotTypeId: {
                    required
                },
                priceMethod: {
                    required
                },
                budget: {
                    required: requiredIf(() => isPriceMethodSelected('budget')),
                    ...isPriceMethodSelected('budget') && {
                        decimal: decimal(2),
                        greaterThan: greaterThan(0)
                    }
                },
                grp: {
                    required: requiredIf(() => isPriceMethodSelected('grp')),
                    ...isPriceMethodSelected('grp') && {
                        ...isOohOrInternetOrder() && {
                            numeric
                        } || {
                            decimal: decimal(2)
                        },
                        greaterThan: greaterThan(0)
                    }
                },
                grpSaleTypeId: {
                    required
                },
                packageTypeId: {
                    required
                },
                targetGroupId: {
                ...!orderType(OrderType.Internet, OrderType.OOH, OrderType.Radio, OrderType.DA) && {
                        required
                    }
                },
                joinInvoices: {}
            },
            orderProducts: {
                date: customValidation((value) => {
                    return !value.length || value.every(orderProduct =>
                        (!orderProduct.validFrom || this.$moment(orderProduct.validFrom, 'L').isSameOrAfter(this.$moment(this.common.validFrom, 'L'))) &&
                        (!orderProduct.validTo || this.$moment(orderProduct.validTo, 'L').isSameOrBefore(this.$moment(this.common.validTo, 'L')))
                    )
                }),
                minLength: customValidation((value) => value && value.length > 0),
                share: customValidation((value) =>
                    value.reduce((share, orderProduct) => ((share*100 + orderProduct.productShare*100)/100), 0) === 1
                )
            },
            orderStationsList: {
                ...(this.selectedPackageType?.minStationCount && !this.selectedPackageType?.stationCount && {
                    minLength: or(
                        customValidation((value, param) => value && value.length >= param, this.selectedPackageType.minStationCount),
                        customValidation(() => !this.allowAddStation)
                    )
                }),
                ...(this.selectedPackageType?.stationCount && {
                    length: or(
                        customValidation((value, param) => value && value.length == param, this.selectedPackageType.stationCount),
                        customValidation(() => !this.allowAddStation)
                    )
                }),
                share: or(
                    customValidation((value) =>
                        value.reduce((share, orderStation) => ((share*100 + orderStation.stationShare*100)/100), 0) === 1),
                    customValidation(() => !this.allowAddStation)
                )
            },
            orderProductsSequences: {
                minElements: customValidation((value) => !value.length || value.every(sequence => sequence.data.length > 1))
            },
            orderAttachments: {}
        }
    }
}
